import React, { useState, useEffect } from 'react';
import axios from 'axios';
const cleanAxios = axios.create();

const ENDPOINT_COLORS = {
  GET: '#34D399',
  POST: '#60A5FA',
  PUT: '#FCD34D',
  DELETE: '#FCA5A5',
  PATCH: '#A78BFA'
};

const ApiReferenceTab = ({ token }) => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [testModal, setTestModal] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testBody, setTestBody] = useState('{\n  \n}');
  const [testLoading, setTestLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchEndpoints = async () => {
      try {
        const res = await axios.get('/api/admin/api-health/endpoints', { headers });
        setEndpoints(res.data.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchEndpoints();
  }, [token]);

  const groups = [...new Set(endpoints.map(ep => ep.path?.split('/')[3] || 'other'))];

  const filtered = endpoints.filter(ep => {
    const matchSearch = !search || ep.path?.toLowerCase().includes(search.toLowerCase()) || ep.description?.toLowerCase().includes(search.toLowerCase());
    const matchGroup = selectedGroup === 'all' || ep.path?.startsWith(`/api/mobile/${selectedGroup}`);
    return matchSearch && matchGroup;
  });

  const grouped = filtered.reduce((acc, ep) => {
    const group = ep.path?.split('/')[3] || 'other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(ep);
    return acc;
  }, {});

  const resolvePath = (path) => {
    return path.replace(/:(\w+)/g, (_, p) => {
      const defaults = { id: '1', branchId: '1', staffId: '1', productId: '1', serviceId: '1', customerId: '1', appointmentId: '1', paymentId: '1', notificationId: '1', fileId: '1' };
      return defaults[p] || '1';
    });
  };

  const runTest = async (ep) => {
    setTestLoading(true);
    setTestResult(null);
    try {
      let body;
      try { body = JSON.parse(testBody); } catch { body = {}; }

      const resolvedPath = resolvePath(ep.path);
      const config = {
        method: ep.method.toLowerCase(),
        url: `${window.location.origin}${resolvedPath}`,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        data: ['POST', 'PUT', 'PATCH'].includes(ep.method) ? body : undefined,
        timeout: 10000
      };

      const start = Date.now();
      const res = await cleanAxios(config);
      setTestResult({
        status: res.status,
        statusText: res.statusText,
        data: res.data,
        time: Date.now() - start
      });
    } catch (e) {
      setTestResult({
        status: e.response?.status || 0,
        statusText: e.response?.statusText || 'Network Error',
        data: e.response?.data || e.message,
        time: 0
      });
    }
    finally { setTestLoading(false); }
  };

  if (loading) return <div className="empty">Yükleniyor...</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, marginBottom: 0 }}>
          <input className="search-input" placeholder="🔍 Endpoint ara..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}
          style={{ padding: '10px 14px', background: 'rgba(26,26,36,0.6)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', color: '#E5E7EB', fontSize: '14px', outline: 'none' }}>
          <option value="all">Tüm Gruplar</option>
          {groups.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
        </select>
      </div>

      {Object.entries(grouped).map(([group, eps]) => (
        <div key={group} className="card" style={{ marginBottom: '12px', padding: '16px 20px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            📁 {group.charAt(0).toUpperCase() + group.slice(1)}
          </div>
          {eps.map((ep, i) => (
            <div key={i}>
              <div
                onClick={() => setExpanded(expanded === `${group}_${i}` ? null : `${group}_${i}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0',
                  cursor: 'pointer', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                  transition: 'background 0.15s'
                }}
              >
                <span style={{
                  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                  color: '#FFFFFF', background: ENDPOINT_COLORS[ep.method] || '#6B7280', minWidth: '55px', textAlign: 'center'
                }}>
                  {ep.method}
                </span>
                <code style={{ flex: 1, fontSize: '13px', color: '#E5E7EB' }}>{ep.path}</code>
                <span style={{ fontSize: '12px', color: '#6B7280', flex: 1 }}>{ep.description}</span>
                {ep.last_24h?.call_count > 0 && (
                  <span style={{ fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                    🔴 {ep.last_24h.call_count} istek
                    {ep.last_24h.error_count > 0 && <span style={{ color: '#FCA5A5', marginLeft: '4px' }}>⚠️ {ep.last_24h.error_count}</span>}
                  </span>
                )}
                <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{expanded === `${group}_${i}` ? '▲' : '▶'}</span>
              </div>

              {expanded === `${group}_${i}` && (
                <div style={{ padding: '12px 0 12px 65px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px' }}>
                    <span style={{ color: '#6B7280' }}>Auth: <span style={{ color: ep.auth ? '#FCD34D' : '#34D399' }}>{ep.auth ? '✅ Gerekli' : '❌ Gerekmez'}</span></span>
                    {ep.last_24h && (
                      <>
                        <span style={{ color: '#6B7280' }}>Ort. Süre: <span style={{ color: '#60A5FA' }}>{ep.last_24h.avg_response_time_ms}ms</span></span>
                      </>
                    )}
                  </div>
                  <button
                    className="btn-approve"
                    onClick={() => { setTestModal(ep); setTestBody(ep.method === 'POST' ? '{\n  "test": true\n}' : '{\n  \n}'); setTestResult(null); }}
                    style={{ fontSize: '12px', padding: '6px 14px' }}
                  >
                    ▶ Test Et
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {testModal && (
        <div className="modal-overlay" onClick={() => setTestModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <h3>🧪 API Test</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{
                padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '700',
                color: '#FFFFFF', background: ENDPOINT_COLORS[testModal.method] || '#6B7280'
              }}>{testModal.method}</span>
              <code style={{ color: '#E5E7EB' }}>{testModal.path}</code>
            </div>

            {['POST', 'PUT', 'PATCH'].includes(testModal.method) && (
              <div className="form-group">
                <label>Request Body (JSON)</label>
                <textarea
                  value={testBody}
                  onChange={e => setTestBody(e.target.value)}
                  rows={6}
                  style={{ fontFamily: "'Monaco','Menlo','Ubuntu Mono',monospace", fontSize: '13px' }}
                />
              </div>
            )}

            <button className="btn-approve" onClick={() => runTest(testModal)} disabled={testLoading} style={{ marginBottom: '16px' }}>
              {testLoading ? 'Gönderiliyor...' : '🚀 İstek Gönder'}
            </button>

            {testResult && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: testResult.status < 400 ? '#34D399' : '#FCA5A5' }}>
                    {testResult.status} {testResult.statusText}
                  </span>
                  {testResult.time > 0 && <span style={{ fontSize: '12px', color: '#6B7280' }}>⏱ {testResult.time}ms</span>}
                </div>
                <pre style={{
                  background: 'rgba(26,26,36,0.6)', padding: '16px', borderRadius: '10px',
                  fontSize: '12px', color: '#E5E7EB', overflow: 'auto', maxHeight: '300px',
                  fontFamily: "'Monaco','Menlo','Ubuntu Mono',monospace", whiteSpace: 'pre-wrap', margin: 0
                }}>
                  {JSON.stringify(testResult.data, null, 2)}
                </pre>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button className="btn-cancel" onClick={() => setTestModal(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiReferenceTab;
